import { useState, useEffect, useRef, useMemo } from "react";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  LineChart, Line, Area, AreaChart,
} from "recharts";
import {
  Droplets, Wheat, Pill, Fuel, Home, AlertTriangle, TrendingUp, TrendingDown, Minus,
  ShieldAlert, Lock, ChevronDown, ChevronUp, DollarSign, Activity, Siren,
} from "lucide-react";

type Trend = "worsening" | "stable" | "improving";
type Recommendation = "STOCKPILE NOW" | "MONITOR" | "STABLE";

interface CountryData {
  country: string;
  flag: string;
  region: string;
  water: number;
  food: number;
  medicine: number;
  fuel: number;
  shelter: number;
  crisisScore: number;
  recommendation: Recommendation;
  trend: Trend;
  note: string;
}

const COUNTRIES: CountryData[] = [
  { country: "Yemen", flag: "🇾🇪", region: "Middle East", water: 89, food: 82, medicine: 78, fuel: 71, shelter: 65, crisisScore: 79, recommendation: "STOCKPILE NOW", trend: "worsening", note: "Ongoing blockade limiting all supply routes; 21M need humanitarian aid" },
  { country: "Somalia", flag: "🇸🇴", region: "East Africa", water: 85, food: 80, medicine: 74, fuel: 62, shelter: 70, crisisScore: 76, recommendation: "STOCKPILE NOW", trend: "worsening", note: "Severe drought cycle; Al-Shabaab controls key supply corridors" },
  { country: "South Sudan", flag: "🇸🇸", region: "East Africa", water: 82, food: 84, medicine: 76, fuel: 68, shelter: 72, crisisScore: 77, recommendation: "STOCKPILE NOW", trend: "worsening", note: "Flooding and conflict displaced 2.2M; food production collapsed" },
  { country: "Afghanistan", flag: "🇦🇫", region: "Central Asia", water: 78, food: 75, medicine: 80, fuel: 55, shelter: 60, crisisScore: 72, recommendation: "STOCKPILE NOW", trend: "stable", note: "Banking collapse limits imports; WHO reports 18M without healthcare access" },
  { country: "Syria", flag: "🇸🇾", region: "Middle East", water: 72, food: 68, medicine: 71, fuel: 75, shelter: 62, crisisScore: 70, recommendation: "STOCKPILE NOW", trend: "stable", note: "Infrastructure destroyed; fuel shortages worsen winter conditions" },
  { country: "Haiti", flag: "🇭🇹", region: "Caribbean", water: 74, food: 70, medicine: 68, fuel: 65, shelter: 58, crisisScore: 68, recommendation: "MONITOR", trend: "worsening", note: "Gang violence blocks 80% of Port-au-Prince supply routes" },
  { country: "Sudan", flag: "🇸🇩", region: "North Africa", water: 70, food: 76, medicine: 65, fuel: 60, shelter: 55, crisisScore: 66, recommendation: "MONITOR", trend: "worsening", note: "Civil war has cut off aid to Darfur; famine declared in multiple regions" },
  { country: "Gaza", flag: "🇵🇸", region: "Middle East", water: 92, food: 88, medicine: 90, fuel: 85, shelter: 88, crisisScore: 89, recommendation: "STOCKPILE NOW", trend: "worsening", note: "Near-total blockade; WHO reports healthcare system collapse" },
  { country: "Ethiopia", flag: "🇪🇹", region: "East Africa", water: 62, food: 65, medicine: 58, fuel: 45, shelter: 42, crisisScore: 55, recommendation: "MONITOR", trend: "improving", note: "Post-Tigray ceasefire improving access; drought remains in south" },
  { country: "DRC", flag: "🇨🇩", region: "Central Africa", water: 65, food: 60, medicine: 62, fuel: 50, shelter: 55, crisisScore: 59, recommendation: "MONITOR", trend: "stable", note: "M23 conflict disrupts eastern supply chains; cobalt mining areas affected" },
  { country: "Myanmar", flag: "🇲🇲", region: "Southeast Asia", water: 55, food: 58, medicine: 60, fuel: 48, shelter: 52, crisisScore: 55, recommendation: "MONITOR", trend: "worsening", note: "Civil war restricts aid; banking system near-collapse" },
  { country: "Lebanon", flag: "🇱🇧", region: "Middle East", water: 60, food: 52, medicine: 55, fuel: 58, shelter: 35, crisisScore: 52, recommendation: "MONITOR", trend: "stable", note: "Currency collapsed 98%; medicine imports critically reduced" },
  { country: "Venezuela", flag: "🇻🇪", region: "South America", water: 50, food: 55, medicine: 62, fuel: 30, shelter: 28, crisisScore: 46, recommendation: "MONITOR", trend: "improving", note: "Partial economic recovery; medicine shortages persist in rural areas" },
  { country: "Pakistan", flag: "🇵🇰", region: "South Asia", water: 55, food: 48, medicine: 42, fuel: 40, shelter: 45, crisisScore: 46, recommendation: "MONITOR", trend: "stable", note: "Post-flood recovery ongoing; water infrastructure 40% damaged" },
  { country: "Ukraine", flag: "🇺🇦", region: "Eastern Europe", water: 40, food: 35, medicine: 38, fuel: 52, shelter: 55, crisisScore: 44, recommendation: "MONITOR", trend: "stable", note: "Energy infrastructure targeted; eastern regions face severe shortages" },
  { country: "Nigeria", flag: "🇳🇬", region: "West Africa", water: 45, food: 50, medicine: 40, fuel: 35, shelter: 30, crisisScore: 40, recommendation: "MONITOR", trend: "stable", note: "Naira devaluation driving food inflation; northeast insurgency continues" },
  { country: "Madagascar", flag: "🇲🇬", region: "East Africa", water: 52, food: 48, medicine: 38, fuel: 30, shelter: 25, crisisScore: 39, recommendation: "STABLE", trend: "stable", note: "Chronic drought in south; limited healthcare infrastructure" },
  { country: "Chad", flag: "🇹🇩", region: "Central Africa", water: 58, food: 52, medicine: 45, fuel: 35, shelter: 40, crisisScore: 46, recommendation: "MONITOR", trend: "worsening", note: "Sudanese refugee influx straining resources; Lake Chad shrinking" },
  { country: "Burkina Faso", flag: "🇧🇫", region: "West Africa", water: 48, food: 55, medicine: 42, fuel: 38, shelter: 35, crisisScore: 44, recommendation: "MONITOR", trend: "worsening", note: "Jihadist insurgency blocks supply to 40% of territory" },
  { country: "Mali", flag: "🇲🇱", region: "West Africa", water: 45, food: 50, medicine: 40, fuel: 35, shelter: 32, crisisScore: 41, recommendation: "MONITOR", trend: "stable", note: "Post-coup instability; northern regions largely inaccessible" },
];

const SORTED = [...COUNTRIES].sort((a, b) => b.crisisScore - a.crisisScore);

const CATEGORY_ICONS = {
  water: Droplets,
  food: Wheat,
  medicine: Pill,
  fuel: Fuel,
  shelter: Home,
};

const CATEGORY_LABELS: Record<string, string> = {
  water: "Water",
  food: "Food",
  medicine: "Medicine",
  fuel: "Fuel",
  shelter: "Shelter",
};

const getCellColor = (val: number) => {
  if (val >= 70) return "bg-destructive/20 text-destructive";
  if (val >= 40) return "bg-yellow-500/20 text-yellow-400";
  return "bg-green-500/20 text-green-400";
};

const getRecBadge = (rec: Recommendation) => {
  if (rec === "STOCKPILE NOW")
    return <Badge className="bg-destructive/20 text-destructive border-destructive/30 animate-pulse text-[10px]"><ShieldAlert className="h-3 w-3 mr-1" />{rec}</Badge>;
  if (rec === "MONITOR")
    return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 text-[10px]"><AlertTriangle className="h-3 w-3 mr-1" />{rec}</Badge>;
  return <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-[10px]">{rec}</Badge>;
};

const getTrendIcon = (trend: Trend) => {
  if (trend === "worsening") return <TrendingDown className="h-3.5 w-3.5 text-destructive" />;
  if (trend === "improving") return <TrendingUp className="h-3.5 w-3.5 text-green-400" />;
  return <Minus className="h-3.5 w-3.5 text-muted-foreground" />;
};

const SPIKE_THRESHOLD = 5; // percent

const EssentialGoodsScarcity = () => {
  const { isPremium } = useAuth();
  const { permission, sendNotification } = usePushNotifications();
  const [selectedCountry, setSelectedCountry] = useState(SORTED[0].country);
  const [showAll, setShowAll] = useState(false);
  const notifiedRef = useRef<Set<string>>(new Set());

  const { data: priceData, isLoading: pricesLoading } = useQuery({
    queryKey: ["essential-prices"],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("fetch-essential-prices");
      if (error) throw error;
      return data as { realtime: any[]; historical: any[]; fetchedAt: string };
    },
    refetchInterval: isPremium ? 30000 : 300000,
    staleTime: 60000,
  });

  // Detect price spikes and fire toast alerts
  const spikeAlerts = useMemo(() => {
    if (!priceData?.realtime) return [];
    return priceData.realtime.filter(
      (item: any) => Math.abs(item.changePercent ?? 0) >= SPIKE_THRESHOLD
    );
  }, [priceData]);

  useEffect(() => {
    for (const spike of spikeAlerts) {
      if (notifiedRef.current.has(spike.symbol)) continue;
      notifiedRef.current.add(spike.symbol);
      const dir = spike.changePercent > 0 ? "surged" : "dropped";
      toast.error(`⚠️ Price Spike: ${spike.name} ${dir} ${Math.abs(spike.changePercent).toFixed(1)}%`, {
        description: `Current price: $${spike.price?.toFixed(2)} — consider adjusting supply strategy`,
        duration: 8000,
      });
      // Browser push notification for price spikes
      if (permission === "granted") {
        sendNotification(
          `⚠️ PRICE SPIKE: ${spike.name} ${dir} ${Math.abs(spike.changePercent).toFixed(1)}%`,
          {
            body: `💰 $${spike.price?.toFixed(2)} | ${spike.category?.toUpperCase() ?? "COMMODITY"}\nConsider adjusting supply strategy`,
            tag: `price-spike-${spike.symbol}`,
            requireInteraction: true,
          }
        );
      }
    }
  }, [spikeAlerts, permission, sendNotification]);

  const visibleCountries = isPremium || showAll ? SORTED : SORTED.slice(0, 5);
  const selected = COUNTRIES.find((c) => c.country === selectedCountry) ?? SORTED[0];

  const radarData = (["water", "food", "medicine", "fuel", "shelter"] as const).map((k) => ({
    category: CATEGORY_LABELS[k],
    value: selected[k],
    fullMark: 100,
  }));

  const barData = SORTED.slice(0, 10).map((c) => ({
    name: `${c.flag} ${c.country}`,
    score: c.crisisScore,
    fill:
      c.crisisScore >= 70
        ? "hsl(var(--destructive))"
        : c.crisisScore >= 40
        ? "hsl(45 93% 47%)"
        : "hsl(142 71% 45%)",
  }));

  return (
    <section className="py-6 sm:py-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="flex items-start gap-2 sm:gap-3 mb-4 sm:mb-6">
          <div className="p-1.5 sm:p-2 rounded-lg bg-destructive/10 shrink-0">
            <ShieldAlert className="h-4 w-4 sm:h-5 sm:w-5 text-destructive" />
          </div>
          <div className="min-w-0">
            <h2 className="text-base sm:text-xl font-bold text-foreground flex items-center gap-2 flex-wrap">
              Essential Goods Scarcity
              <Badge className="bg-destructive/20 text-destructive border-destructive/30 text-[9px] sm:text-[10px]">LIVE</Badge>
            </h2>
            <p className="text-[10px] sm:text-xs text-muted-foreground">Water · Food · Medicine · Fuel · Shelter — scarcity by country</p>
          </div>
        </div>

        {/* Price Spike Alert Banner */}
        {spikeAlerts.length > 0 && (
          <div className="mb-4 p-3 rounded-lg border border-destructive/40 bg-destructive/5 animate-pulse">
            <div className="flex items-center gap-2 mb-2">
              <Siren className="h-4 w-4 text-destructive" />
              <span className="text-xs font-bold text-destructive">
                ⚠️ PRICE SPIKE ALERT — {spikeAlerts.length} commodit{spikeAlerts.length === 1 ? "y" : "ies"} moved &gt;{SPIKE_THRESHOLD}% today
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {spikeAlerts.map((s: any) => (
                <Badge
                  key={s.symbol}
                  className="bg-destructive/15 text-destructive border-destructive/30 text-[10px] font-mono"
                >
                  {s.name}: {s.changePercent > 0 ? "+" : ""}{s.changePercent?.toFixed(1)}% → ${s.price?.toFixed(2)}
                </Badge>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {/* Radar Chart — selected country profile */}
          <Card className="border-border/50 bg-card/50 backdrop-blur">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center justify-between">
                <span>{selected.flag} {selected.country} Scarcity Profile</span>
                {getRecBadge(selected.recommendation)}
              </CardTitle>
              <p className="text-[10px] text-muted-foreground">{selected.note}</p>
            </CardHeader>
            <CardContent>
              <div className="h-[200px] sm:h-[240px]">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="65%">
                    <PolarGrid stroke="hsl(var(--border))" />
                    <PolarAngleAxis dataKey="category" tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }} />
                    <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 8 }} />
                    <Radar name="Scarcity %" dataKey="value" stroke="hsl(var(--destructive))" fill="hsl(var(--destructive))" fillOpacity={0.25} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
              {/* Country selector */}
              <div className="flex flex-wrap gap-1 mt-2">
                {SORTED.slice(0, isPremium ? 20 : 5).map((c) => (
                  <button
                    key={c.country}
                    onClick={() => setSelectedCountry(c.country)}
                    className={`text-[10px] px-2 py-0.5 rounded-full border transition-colors ${
                      selectedCountry === c.country
                        ? "bg-primary/15 text-primary border-primary/30"
                        : "border-border/50 text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    {c.flag} {c.country}
                  </button>
                ))}
                {!isPremium && (
                  <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                    <Lock className="h-3 w-3" /> +15 more
                  </span>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Bar Chart — Top 10 crisis scores */}
          <Card className="border-border/50 bg-card/50 backdrop-blur">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Global Crisis Score Ranking</CardTitle>
              <p className="text-[10px] text-muted-foreground">Top 10 countries by composite scarcity index</p>
            </CardHeader>
            <CardContent>
              <div className="h-[250px] sm:h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={barData} layout="vertical" margin={{ left: 5, right: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.3} />
                    <XAxis type="number" domain={[0, 100]} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 9 }} />
                    <YAxis type="category" dataKey="name" width={90} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 9 }} />
                    <Tooltip
                      contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 11 }}
                      formatter={(val: number) => [`${val}%`, "Crisis Score"]}
                    />
                    <Bar dataKey="score" radius={[0, 4, 4, 0]}>
                      {barData.map((entry, i) => (
                        <Cell key={i} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Heat Table */}
          <Card className="border-border/50 bg-card/50 backdrop-blur lg:col-span-1">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center justify-between">
                Scarcity Matrix
                <div className="flex gap-2 text-[9px] text-muted-foreground">
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500" />{"<40%"}</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-500" />40-70%</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-destructive" />{">70%"}</span>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
               <div className="overflow-auto max-h-[280px] sm:max-h-[340px]">
                <table className="w-full text-[9px] sm:text-[10px]">
                  <thead className="sticky top-0 bg-card z-10">
                    <tr className="border-b border-border/30">
                      <th className="text-left py-1.5 px-2 text-muted-foreground font-medium">Country</th>
                      {Object.entries(CATEGORY_ICONS).map(([key, Icon]) => (
                        <th key={key} className="text-center py-1.5 px-1 text-muted-foreground font-medium">
                          <Icon className="h-3 w-3 mx-auto" />
                        </th>
                      ))}
                      <th className="text-center py-1.5 px-1 text-muted-foreground font-medium">Trend</th>
                      <th className="text-center py-1.5 px-2 text-muted-foreground font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleCountries.map((c) => (
                      <tr
                        key={c.country}
                        className="border-b border-border/10 hover:bg-accent/30 cursor-pointer transition-colors"
                        onClick={() => setSelectedCountry(c.country)}
                      >
                        <td className="py-1.5 px-2 font-medium text-foreground whitespace-nowrap">
                          {c.flag} {c.country}
                        </td>
                        {(["water", "food", "medicine", "fuel", "shelter"] as const).map((k) => (
                          <td key={k} className="text-center py-1.5 px-1">
                            <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-mono font-bold ${getCellColor(c[k])}`}>
                              {c[k]}%
                            </span>
                          </td>
                        ))}
                        <td className="text-center py-1.5 px-1">{getTrendIcon(c.trend)}</td>
                        <td className="text-center py-1.5 px-2">{getRecBadge(c.recommendation)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!isPremium && !showAll && (
                <div className="p-3 text-center border-t border-border/30">
                  <Button variant="outline" size="sm" className="text-[10px] h-7" onClick={() => setShowAll(true)}>
                    <ChevronDown className="h-3 w-3 mr-1" /> Show all 20 countries
                  </Button>
                </div>
              )}
              {showAll && !isPremium && (
                <div className="p-3 text-center border-t border-border/30">
                  <Button variant="outline" size="sm" className="text-[10px] h-7" onClick={() => setShowAll(false)}>
                    <ChevronUp className="h-3 w-3 mr-1" /> Collapse
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Intelligence notes */}
        <Card className="mt-4 border-border/50 bg-card/50 backdrop-blur">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-yellow-400" />
              Scarcity Intelligence Notes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {SORTED.slice(0, isPremium ? 10 : 4).map((c) => (
                <div key={c.country} className="flex items-start gap-2 p-2 rounded-lg bg-accent/20 border border-border/20">
                  <span className="text-lg">{c.flag}</span>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-foreground">{c.country}</span>
                      <span className="text-[9px] text-muted-foreground">Score: {c.crisisScore}%</span>
                      {getTrendIcon(c.trend)}
                    </div>
                    <p className="text-[10px] text-muted-foreground leading-relaxed">{c.note}</p>
                  </div>
                </div>
              ))}
            </div>
            {!isPremium && (
              <p className="text-center text-[10px] text-muted-foreground mt-3 flex items-center justify-center gap-1">
                <Lock className="h-3 w-3" /> Upgrade to Premium for full intelligence notes & AI trend analysis
              </p>
            )}
          </CardContent>
        </Card>

        {/* Real-Time Essential Goods Price Tracker */}
        <Card className="mt-4 border-border/50 bg-card/50 backdrop-blur">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-primary" />
              Real-Time Essential Goods Prices
              <Badge className="bg-primary/20 text-primary border-primary/30 text-[10px]">LIVE</Badge>
              {priceData?.fetchedAt && (
                <span className="text-[9px] text-muted-foreground font-normal ml-auto">
                  Updated: {new Date(priceData.fetchedAt).toLocaleTimeString()}
                </span>
              )}
            </CardTitle>
            <p className="text-[10px] text-muted-foreground">Yahoo Finance real-time + World Bank historical reference prices</p>
          </CardHeader>
          <CardContent>
            {pricesLoading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="h-24 rounded-lg bg-accent/20 animate-pulse" />
                ))}
              </div>
            ) : (
              <>
                {/* Real-time commodity cards */}
                <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3 mb-4">
                  {(priceData?.realtime ?? []).slice(0, isPremium ? 20 : 4).map((item: any) => {
                    const isUp = (item.changePercent ?? 0) > 0;
                    const isAlert = Math.abs(item.changePercent ?? 0) > 3;
                    return (
                      <div
                        key={item.symbol}
                        className={`p-3 rounded-lg border transition-colors ${
                          isAlert
                            ? "border-destructive/40 bg-destructive/5"
                            : "border-border/30 bg-accent/10"
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-[10px] text-muted-foreground font-medium">{item.category.toUpperCase()}</span>
                          {isAlert && <Activity className="h-3 w-3 text-destructive animate-pulse" />}
                        </div>
                        <p className="text-xs font-semibold text-foreground truncate">{item.name}</p>
                        <div className="flex items-baseline gap-1.5 mt-1">
                          <span className="text-sm font-bold font-mono text-foreground">
                            ${item.price?.toFixed(2)}
                          </span>
                          <span
                            className={`text-[10px] font-mono font-semibold ${
                              isUp ? "text-green-400" : "text-destructive"
                            }`}
                          >
                            {isUp ? "▲" : "▼"} {Math.abs(item.changePercent ?? 0).toFixed(2)}%
                          </span>
                        </div>
                        {/* Mini sparkline */}
                        {item.sparkline?.length > 2 && (
                          <div className="h-8 mt-1.5">
                            <ResponsiveContainer width="100%" height="100%">
                              <AreaChart data={item.sparkline}>
                                <defs>
                                  <linearGradient id={`grad-${item.symbol}`} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={isUp ? "hsl(142 71% 45%)" : "hsl(var(--destructive))"} stopOpacity={0.3} />
                                    <stop offset="100%" stopColor={isUp ? "hsl(142 71% 45%)" : "hsl(var(--destructive))"} stopOpacity={0} />
                                  </linearGradient>
                                </defs>
                                <Area
                                  type="monotone"
                                  dataKey="value"
                                  stroke={isUp ? "hsl(142 71% 45%)" : "hsl(var(--destructive))"}
                                  fill={`url(#grad-${item.symbol})`}
                                  strokeWidth={1.5}
                                  dot={false}
                                />
                              </AreaChart>
                            </ResponsiveContainer>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {!isPremium && (priceData?.realtime?.length ?? 0) > 4 && (
                  <p className="text-center text-[10px] text-muted-foreground mb-4 flex items-center justify-center gap-1">
                    <Lock className="h-3 w-3" /> Upgrade to see all {priceData?.realtime?.length} commodities with full sparklines
                  </p>
                )}

                {/* World Bank Historical Reference */}
                {Array.isArray(priceData?.historical) && priceData.historical.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-foreground mb-2 flex items-center gap-1.5">
                      <TrendingUp className="h-3.5 w-3.5 text-muted-foreground" />
                      World Bank Reference Prices (Annual Trend)
                    </h4>
                    <div className="overflow-auto">
                      <table className="w-full text-[10px]">
                        <thead>
                          <tr className="border-b border-border/30">
                            <th className="text-left py-1.5 px-2 text-muted-foreground font-medium">Commodity</th>
                            <th className="text-right py-1.5 px-2 text-muted-foreground font-medium">2023</th>
                            <th className="text-right py-1.5 px-2 text-muted-foreground font-medium">2024</th>
                            <th className="text-right py-1.5 px-2 text-muted-foreground font-medium">2025</th>
                            <th className="text-right py-1.5 px-2 text-muted-foreground font-medium">YoY %</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(priceData.historical as any[]).slice(0, isPremium ? 20 : 5).map((item: any, i: number) => {
                            const trend = item.trend ?? 0;
                            return (
                              <tr key={i} className="border-b border-border/10 hover:bg-accent/20 transition-colors">
                                <td className="py-1.5 px-2 font-medium text-foreground">{item.commodity}</td>
                                <td className="py-1.5 px-2 text-right font-mono text-muted-foreground">{item.price2023}</td>
                                <td className="py-1.5 px-2 text-right font-mono text-muted-foreground">{item.price2024}</td>
                                <td className="py-1.5 px-2 text-right font-mono text-foreground font-semibold">{item.price2025}</td>
                                <td className={`py-1.5 px-2 text-right font-mono font-semibold ${
                                  trend > 0 ? "text-destructive" : "text-green-400"
                                }`}>
                                  {trend > 0 ? "+" : ""}{trend.toFixed(1)}%
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default EssentialGoodsScarcity;
